import { cva, VariantProps } from "class-variance-authority";
import { ChangeEvent } from "react";
import { cn } from "../../../utils";
import { Label } from "../../forms/Label";

const switchVariants = cva(
  "relative shadow peer-focus:outline-none rounded-full peer after:absolute after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-['']",
  {
    variants: {
      size: {
        small:
          // track size + knob size + knob position
          "h-5 w-9 after:h-4 after:w-4 after:start-[2px] after:top-[2px] peer-checked:after:translate-x-4",
        medium:
          "h-6 w-11 after:h-5 after:w-5 after:start-[3px] after:top-[2px] peer-checked:after:translate-x-5",
        large:
          "h-8 w-14 after:h-7 after:w-7 after:start-[4px] after:top-[2px] peer-checked:after:translate-x-7",
        labelled:
          "h-6 w-20 rounded-lg p-1 after:h-4 after:w-6 after:start-1 after:top-1 peer-checked:after:translate-x-11  ",
      },

      active: {
        default:
          "peer-checked:bg-blue-600 peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-checked:bg-blue-600 dark:peer-focus:ring-blue-800",

        green:
          "peer-checked:bg-green-600 peer-checked:after:border-white peer-focus:ring-green-300 dark:peer-checked:bg-green-600 dark:peer-focus:ring-green-800",
        indigo:
          "peer-checked:bg-indigo peer-checked:after:border-white peer-focus:ring-indigo/30",
      },

      inactive: {
        default: "bg-gray-200 dark:bg-gray-700 dark:border-gray-600",
        destructive: "bg-red-500 dark:bg-red-700 dark:border-red-600",
        primary: "bg-primary",
      },
    },

    defaultVariants: {
      size: "medium",
      active: "default",
      inactive: "default",
    },
  },
);

type TSwitchProp = VariantProps<typeof switchVariants> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  disabled?: boolean;
  labels?: {
    checked: string;
    unchecked: string;
  };
  labelClassName?: string;
  ariaLabel?: string;
};

export function Switch({
  size,
  active,
  inactive,
  checked,
  onCheckedChange,
  disabled = false,
  id,
  labels,
  labelClassName,
  ariaLabel,
}: TSwitchProp) {
  return (
    <Label
      className={cn(
        "inline-flex cursor-pointer items-center",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onCheckedChange(e.target.checked)
        }
        id={`${id}-switch-input`}
        aria-label={ariaLabel}
      />

      <div
        className={cn(switchVariants({ size, active, inactive }))}
        data-testid="switch-track"
      >
        {labels && (
          <>
            <span
              className={cn(
                "pointer-events-none absolute top-1 right-2 z-10 text-[10px] font-bold transition-opacity",
                checked ? "opacity-0" : "opacity-100",
                labelClassName ?? "text-primary-foreground",
              )}
            >
              {labels.unchecked}
            </span>
            <span
              className={cn(
                "pointer-events-none absolute top-1 left-2 z-10 text-[10px] font-bold transition-opacity",
                checked ? "opacity-100" : "opacity-0",
                labelClassName ?? "text-indigo-foreground",
              )}
            >
              {labels.checked}
            </span>
          </>
        )}
      </div>
    </Label>
  );
}
